import React from "react";
import { Link } from "react-router-dom";
import _ from "lodash";
import Modal from "Components/smallModal/smallModal";
import Toggle from "Components/toggle/Toggle";
import Button from "Components/button/Button";
import Repeater from "Components/repeater/Repeater";
import WarningBanner from "Components/warningBanner/WarningBanner";
import DeliverySpotBanner from "Components/DeliverySpotBanner";
import TranslatedString from "Components/TranslatedString";
import RadioButton from "Components/radiobutton/RadioButton";
import isEmail from "validator/lib/isEmail";
import { t } from "i18next";
import cx from "classnames";
import TextArea from "Components/textArea/TextArea";
import s from "./generals.scss";
import QuotaSelector from "Components/quotaSelector/QuotaSelector";
import { quotas, humanQuotas } from "../../../config";
import QboxmailApi from "@/qboxmailApi";

const api = new QboxmailApi({ useCookie: true });

export default class Generals extends React.Component {
  state = {
    viewFields: [
      "max_email_quota",
      "pop_disabled",
      "webmail_disabled",
      "imap_disabled",
      "smtp_disabled",
      "activesync",
      "dav",
      "chat",
      "meeting",
      "outbound_limit",
      "forwards",
      "keep_copy",
      "spam_in_inbox",
      "bounce_mail",
      "bounce_msg",
    ],
    isWarnModalOpened: false,
    outboundLimitValue: null,
    isRequestedModalOpened: false,
    isRequestingChange: false,
    lastRequestTimestamp: null,
    canRequest: true,
  };

  componentDidMount() {
    const lastRequestTimestamp = localStorage.getItem(`qb_daily_sending_limit_request_${this.props.emailAccount.code}`);
    let canRequest = true;
    if (lastRequestTimestamp) {
      const lastRequestDate = new Date(lastRequestTimestamp);
      const now = new Date();
      canRequest = now - lastRequestDate > 24 * 60 * 60 * 1000;
      this.setState({ lastRequestTimestamp: lastRequestDate, canRequest });
    } else {
      this.setState({ canRequest });
    }
  }

  componentWillUnmount() {
    this.props.resetFields(...this.state.viewFields);
  }

  openWarnModal = (val) => {
    this.setState({ isWarnModalOpened: true, outboundLimitValue: val });
  };

  requestMessages = async () => {
    this.setState({
      isRequestingChange: true,
    });

    try {
      await api.requestAdditionalService("daily_sending_limit");
      const now = new Date();
      localStorage.setItem(`qb_daily_sending_limit_request_${this.props.emailAccount.code}`, now);
      this.setState({ lastRequestTimestamp: now, canRequest: false });
    } catch (err) {
      console.log(err);
    }

    this.setState({
      isRequestingChange: false,
      isRequestedModalOpened: true,
    });
  };

  closeWarnModal = () => {
    this.props.setFormFieldValue({ outbound_limit: this.state.outboundLimitValue });

    this.setState({
      isWarnModalOpened: false,
      paidOptionSelected: true,
      outboundLimitValue: null,
    });
  };

  alertIfIsPaidOption = (values) => {
    let val = parseInt(values.outbound_limit, 10);
    const isAPaidOption = !!this.props.emailOutboundLimits.filter((limit) => limit.id === val && limit.pay)[0];

    if (isAPaidOption && !this.props.isLoggedUserAdmin) {
      return this.openWarnModal(val);
    }

    this.setState({ paidOptionSelected: false });
    this.props.setFormFieldValue(values);
  };

  setForwards = (values) => {
    // if forwards is unsetted, uncheck the keep_copy option
    // if not, uncheck bounce mail
    const forwards = values.forwards;
    if (forwards.length === 0) {
      this.props.setFormFieldValue({
        keep_copy: false,
        ...values,
      });
    } else {
      this.props.setFormFieldValue({
        bounce_mail: false,
        ...values,
        keep_copy: true,
      });
    }
  };

  render() {
    const {
      domain,
      emailAccount,
      isFormLoading,
      setFormFieldValue,
      submitForm,
      formData,
      formErrors,
      isLoggedUserTrial,
      areEmailOutboundLimitsLoading,
      emailOutboundLimits,
      loggedUser,
      loggedUserType,
      isLoggedUserAdmin,
      domain: {
        plan: { features },
      },
    } = this.props;

    const { lastRequestTimestamp, canRequest } = this.state;

    let hasForwards = false;

    if (formData.forwards.length > 0) hasForwards = true;

    const bounceToggleDisabled = hasForwards || emailAccount.autoresponder_enabled || formData.spam_in_inbox;

    let isWaitingPayment = loggedUser.type === "Customer" && loggedUser.status_detail === "wait_pay";

    let isAnnual = false;
    if (
      loggedUser.type === "Customer" &&
      loggedUser.orders?.[0]?.billing_cycle !== "monthly" &&
      !isLoggedUserTrial &&
      !isWaitingPayment &&
      window.railsEnv !== "sandbox"
    ) {
      isAnnual = true;
    }

    // disable options that exceed the domain limits
    let disabledQuotas = [];
    _.each(quotas, (v, k) => {
      if (v.value > domain.max_email_quota) {
        disabledQuotas.push(v.value);
      }
    });

    const domainMaxQuota = _.filter(quotas, {
      value: domain.max_email_quota,
    })[0];

    let outboundLimitOptions = [];
    if (!areEmailOutboundLimitsLoading) {
      let defaultOption = null;

      outboundLimitOptions = _.compact(
        emailOutboundLimits.map((limit) => {
          const discontinuedLabel = limit.discontinued ? <code>[{t("input.hint.discontinued")}]</code> : null;
          const includedLabel = limit.included ? <code>[{t("input.hint.included")}]</code> : null;
          const paymentOptionLabel = limit.pay > 0 ? <code>[{t("input.hint.paid.option")}]</code> : null;

          let msgNumber = limit.rate;
          let hours = limit.period / 3600;

          let label = null;
          if (limit.description === "email_default") {
            // show text like this: "limiti di invio come dominio [1000 messaggi ogni 24 ore]"
            label = (
              <TranslatedString
                subs={{
                  msgNumber: new Intl.NumberFormat().format(msgNumber),
                  hours,
                }}
                components={{
                  OptionTag: (
                    <code>
                      <TranslatedString
                        subs={{
                          msgNumber: new Intl.NumberFormat().format(limit.domain_rate),
                          hours: 24,
                        }}
                        components={{
                          OptionTag: <span></span>,
                        }}
                      >
                        input.label.sentMsg24h
                      </TranslatedString>
                    </code>
                  ),
                }}
              >
                input.label.sentMsg24h.domainDefault
              </TranslatedString>
            );
          } else {
            // show text like this: "1000 messaggi ogni 24 ore [Compreso nel piano]"
            label = (
              <TranslatedString
                subs={{
                  msgNumber: new Intl.NumberFormat().format(msgNumber),
                  hours,
                }}
                components={{
                  OptionTag: includedLabel || discontinuedLabel || paymentOptionLabel || <></>,
                }}
              >
                input.label.sentMsg24h
              </TranslatedString>
            );
          }

          let data = {
            disabled: limit.disabled,
            value: limit.id,
            rate: limit.rate,
            label,
          };

          if (limit.description === "email_default") {
            defaultOption = data;
            return null;
          }

          return data;
        })
      );

      outboundLimitOptions = _.sortBy(outboundLimitOptions, ["rate"]);
      outboundLimitOptions.unshift(defaultOption);
    }

    return (
      <form className={s.general} method="post" onSubmit={(e) => submitForm(e, this.state.viewFields)}>
        <div className={s.wrapInfoBtn}>
          <div className={s.warnignMsg}>
            {!formErrors.onForm && !this.props.areFieldsPristine(...this.state.viewFields) && (
              <WarningBanner color="yellow-text" msg={<TranslatedString>infoMsg.unsaveChanged</TranslatedString>} />
            )}
            {formErrors.onForm && <WarningBanner msg={formErrors.onForm} color="red" />}
          </div>
          <div className={s.infoBtnGroup}>
            <Button className={s.docsBtn} docsKey="emailAccount_settings" color="docs" type="button">
              button.docs
            </Button>
            <Button className={s.saveBtn} type="submit" loading={isFormLoading} disabled={!this.props.canEdit}>
              button.save
            </Button>
          </div>
        </div>

        <div className={s.settings}>
          {emailAccount.name !== "postmaster" && loggedUserType !== "EmailAccount" && (
            <div className="sectionSP">
              <div className="infoSectionSP">
                <div className="settingTitle">{t("settings.sections.emailAccout.availableSpace")}</div>
                <div className="sectionDesc">{t("settings.sections.account.limits.desc.emailAccount")}</div>
              </div>
              <div className="formsSectionSP">
                <div className="inputFull2">
                  <WarningBanner
                    info
                    color="blue"
                    msg={
                      domainMaxQuota && emailAccount.name !== "postmaster" ? (
                        <TranslatedString subs={{ maxSize: domainMaxQuota.label }}>
                          input.hint.account.size
                        </TranslatedString>
                      ) : (
                        <TranslatedString>infoMsg.maxQuota</TranslatedString>
                      )
                    }
                  />
                </div>

                <QuotaSelector
                  className="inputFull2"
                  onChange={setFormFieldValue}
                  value={formData.max_email_quota}
                  name="max_email_quota"
                  errors={formErrors.max_email_quota}
                  hideUpgradeL={!features.mailbox_50gb}
                  hideUpgradeXL={!features.mailbox_100gb}
                />

                {/*                <Select
                  className="inputFull2"
                  label={t("input.label.maxAccountsSize")}
                  components={{ DropdownIndicator }}
                  name="max_email_quota"
                  clearable={false}
                  disabled={!this.props.canEdit}
                  options={optionsQuota}
                  onChange={setFormFieldValue}
                  value={formData.max_email_quota}
                />*/}
              </div>
            </div>
          )}

          {loggedUserType !== "EmailAccount" && loggedUserType !== "Postmaster" && (
            <div className="sectionSP">
              <div className="infoSectionSP">
                <div className="settingTitle">{t("settings.sections.sentLimits.title")}</div>
                <div className="sectionDesc">{t("settings.sections.sentLimits.desc.emailAccount")}</div>
              </div>
              <div className="formsSectionSP">
                {areEmailOutboundLimitsLoading ? (
                  t("infoMsg.loading.limits")
                ) : (
                  <RadioButton
                    className="inputFull2"
                    grouplabel={t("input.label.account.limits.sending")}
                    children={outboundLimitOptions}
                    name="outbound_limit"
                    value={parseInt(formData.outbound_limit, 10)}
                    onChange={this.alertIfIsPaidOption}
                    disabled={
                      isLoggedUserTrial ||
                      isWaitingPayment ||
                      (!isLoggedUserAdmin && !loggedUser.can_edit_outbound_limits)
                    }
                  />
                )}

                <Link className="text-sm" to={`/prices`}>
                  {t("showPrices")}
                </Link>

                {isAnnual && (
                  <div className="flex items-center justify-between gap-4 inputFull2">
                    <div className="flex flex-col gap-2">
                      <p className="text-sm">{t("infoMsg.annualSendingLimit")}</p>
                      {!canRequest && (
                        <p className="text-sm">
                          {t("infoMsg.requestSent", {
                            date: lastRequestTimestamp.toLocaleDateString(),
                            time: lastRequestTimestamp.toLocaleTimeString(),
                          })}
                        </p>
                      )}
                    </div>

                    <Button
                      onClick={() => {
                        this.requestMessages();
                      }}
                      loading={this.state.isRequestingChange}
                      type="button"
                      disabled={!canRequest}
                    >
                      prices.request
                    </Button>
                  </div>
                )}

                {this.state.paidOptionSelected && (
                  <div className={cx(s.payOptionMsg, "inputFull2")}>
                    <WarningBanner msg={t("infoMsg.payOption.invoice")} />
                  </div>
                )}

                {(isLoggedUserTrial || isWaitingPayment) && (
                  <div className={cx(s.payOptionMsg, "inputFull2")}>
                    <WarningBanner msg={t("infoMsg.disabledWhileWaitPaid")} info color="blue" />
                  </div>
                )}
                <div className={cx(s.payOptionMsg, "inputFull2", "mt-6")}>
                  <DeliverySpotBanner hideCTA={isLoggedUserTrial || isWaitingPayment} />
                </div>
              </div>
            </div>
          )}

          {loggedUserType !== "EmailAccount" && (
            <div className="sectionSP">
              <div className="infoSectionSP">
                <div className="settingTitle">{t("settings.sections.services.title")}</div>
                <div className="sectionDesc">
                  <TranslatedString>settings.sections.services.desc.emailAccount</TranslatedString>
                </div>
              </div>
              <div className="formsSectionSP">
                <Toggle
                  className="inputFull2"
                  label={t("input.label.access.pop")}
                  name="pop_disabled"
                  reverse
                  checked={domain.pop_disabled ? true : formData.pop_disabled}
                  disabled={domain.pop_disabled || !this.props.canEdit}
                  hint={domain.pop_disabled ? t("input.hint.disabled.onDomain") : ""}
                  onChange={setFormFieldValue}
                />
                <Toggle
                  className="inputFull2"
                  label={t("input.label.access.imap")}
                  name="imap_disabled"
                  reverse
                  checked={domain.imap_disabled ? true : formData.imap_disabled}
                  disabled={domain.imap_disabled || !this.props.canEdit}
                  hint={domain.imap_disabled ? t("input.hint.disabled.onDomain") : ""}
                  onChange={setFormFieldValue}
                />

                <Toggle
                  className="inputFull2"
                  label={t("input.label.access.smtp")}
                  name="smtp_disabled"
                  reverse
                  checked={domain.smtp_disabled ? true : formData.smtp_disabled}
                  disabled={domain.smtp_disabled || !this.props.canEdit}
                  hint={domain.smtp_disabled ? t("input.hint.disabled.onDomain") : ""}
                  onChange={setFormFieldValue}
                />

                <Toggle
                  className="inputFull2"
                  label={t("input.label.access.webmail")}
                  name="webmail_disabled"
                  reverse
                  checked={domain.webmail_disabled ? true : formData.webmail_disabled}
                  disabled={domain.webmail_disabled || !this.props.canEdit}
                  hint={domain.webmail_disabled ? t("input.hint.disabled.onDomain") : ""}
                  onChange={setFormFieldValue}
                />

                {features.dav && (
                  <Toggle
                    className="inputFull2"
                    label={t("input.label.access.dav")}
                    name="dav"
                    checked={formData.dav}
                    errors={formErrors.dav}
                    onChange={setFormFieldValue}
                    disabled={!domain.dav || !this.props.canEdit}
                    hint={!domain.dav ? t("input.hint.disabled.onDomain") : ""}
                  />
                )}

                {features.activesync && emailAccount.name !== "postmaster" && (
                  <Toggle
                    className="inputFull2"
                    label={t("input.label.access.active_sync")}
                    name="activesync"
                    checked={formData.activesync}
                    errors={formErrors.activesync}
                    disabled={!domain.activesync || !this.props.canEdit}
                    hint={!domain.activesync ? t("input.hint.disabled.onDomain") : ""}
                    onChange={setFormFieldValue}
                  />
                )}

                {/*features.chat && (
                  <Toggle
                    className="inputFull2"
                    label={t("input.label.access.chat")}
                    name="chat"
                    checked={formData.chat}
                    errors={formErrors.chat}
                    onChange={setFormFieldValue}
                    disabled={!domain.chat || !this.props.canEdit}
                    hint={!domain.chat ? t("input.hint.disabled.onDomain") : ""}
                  />
                )*/}

                {/*features.meeting && (
                  <Toggle
                    className="inputFull2"
                    label={t("input.label.access.meeting")}
                    name="meeting"
                    checked={formData.meeting}
                    errors={formErrors.meeting}
                    onChange={setFormFieldValue}
                    disabled={!domain.meeting || !this.props.canEdit}
                    hint={!domain.meeting ? t("input.hint.disabled.onDomain") : ""}
                  />
                )*/}
              </div>
            </div>
          )}

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.forwards.title")}</div>
              <div className="sectionDesc">
                <TranslatedString>settings.sections.forwards.desc</TranslatedString>
              </div>
            </div>
            <div className="formsSectionSP">
              <Repeater
                className="inputFull2"
                labelInput={t("input.label.forward.add")}
                label={t("listTable.header.title.forwards")}
                name="forwards"
                errors={formErrors.forwards}
                values={formData.forwards}
                onChange={this.setForwards}
                disabled={!this.props.canEdit}
                validateNewValue={(newVal) => {
                  if (!isEmail(newVal.toLowerCase())) {
                    alert(`${newVal} + ${t("errorMsg.notValid.emailAddress")}`);
                    return false;
                  }
                  return true;
                }}
                placeholder={t("input.placeholder.myOtherEmail")}
              />

              <Toggle
                className="inputFull2"
                label={t("input.label.keepCopy")}
                name="keep_copy"
                errors={formErrors.keep_copy}
                checked={hasForwards ? formData.keep_copy : false}
                onChange={setFormFieldValue}
                disabled={!hasForwards || !this.props.canEdit}
              />
            </div>
          </div>

          <div className="sectionSP">
            <div className="infoSectionSP">
              <div className="settingTitle">{t("settings.sections.other.title")}</div>
            </div>
            <div className="formsSectionSP">
              <Toggle
                className="inputFull2"
                label={t("input.label.spamInInbox")}
                name="spam_in_inbox"
                onChange={setFormFieldValue}
                checked={formData.spam_in_inbox}
                errors={formErrors.spam_in_inbox}
                disabled={formData.bounce_mail || !this.props.canEdit}
                hint={formData.bounce_mail && t("input.hint.disabled.bounce")}
              />
              <Toggle
                className="inputFull2"
                label={t("input.label.enableBounceMsg")}
                name="bounce_mail"
                errors={formErrors.bounce_mail}
                checked={formData.bounce_mail}
                onChange={setFormFieldValue}
                disabled={bounceToggleDisabled || !this.props.canEdit}
                hint={bounceToggleDisabled && t("input.hint.disabled.autoresponderOrForwards")}
              />
              <TextArea
                className="inputFull2"
                name="bounce_msg"
                label={t("input.label.bounceMsg")}
                value={formData.bounce_msg || ""}
                disabled={!formData.bounce_mail || !this.props.canEdit}
                onChange={setFormFieldValue}
                errors={formErrors.bounce_msg}
              />
            </div>
          </div>
        </div>

        <Modal
          className=""
          opened={this.state.isRequestedModalOpened}
          closeModal={() => {
            this.setState({
              isRequestedModalOpened: false,
            });
          }}
          title={t("additionalservices.requested.title")}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: t("generals.requested.desc"),
            }}
          ></div>
          <div className="">
            <Button
              onClick={() => {
                this.setState({
                  isRequestedModalOpened: false,
                });
              }}
              type="button"
            >
              button.ok
            </Button>
          </div>
        </Modal>

        <Modal
          title={t("modal.paidOption.title")}
          closeModal={this.closeWarnModal}
          opened={this.state.isWarnModalOpened}
        >
          <div>
            <TranslatedString>modal.paidOption.desc</TranslatedString>
          </div>

          <div>
            <Button onClick={this.closeWarnModal} type="button">
              button.understood
            </Button>
          </div>
        </Modal>
      </form>
    );
  }
}
